import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { pageMeta } from "@/data/mockData";

// ============================== Helpers ==============================
const fmt = (n) =>
  Number.isFinite(n) && n !== 0
    ? "S$" +
      Number(n).toLocaleString("en-SG", { maximumFractionDigits: 0, minimumFractionDigits: 0 })
    : "S$0";

const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const monthlyPayment = (principal, annualRatePct, tenureYears) => {
  const r = annualRatePct / 100 / 12;
  const n = tenureYears * 12;
  if (principal <= 0 || n <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

// ============================== Field ==============================
function Field({ label, value, onChange, onBlur, prefix, suffix, testid, placeholder, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#0a2540] mb-2">{label}</span>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          data-testid={testid}
          placeholder={placeholder}
          min="0"
          className={`w-full ${prefix ? "pl-12" : "pl-4"} ${suffix ? "pr-16" : "pr-4"} py-3.5 border border-gray-200 rounded-xl text-sm text-[#0a2540] focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white transition-all`}
        />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
      {hint && <span className="block text-xs text-slate-400 mt-1.5">{hint}</span>}
    </label>
  );
}

// ============================== Result Pane ==============================
function ResultPane({ filled, placeholder, primary, primaryLabel, breakdown }) {
  return (
    <div className="bg-gradient-to-br from-[#0a2540] to-[#023e8a] text-white rounded-3xl p-8 md:p-10 lg:sticky lg:top-28">
      {filled ? (
        <>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#ffde59] font-bold mb-4">
            {primaryLabel}
          </div>
          <div className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-none mb-2">
            {primary}
          </div>
          {breakdown && (
            <dl className="mt-10 pt-8 border-t border-white/15 space-y-5">
              {breakdown.map((b) => (
                <div key={b.label} className="flex items-baseline justify-between gap-4">
                  <dt className="text-xs uppercase tracking-wider text-white/60 font-medium">
                    {b.label}
                  </dt>
                  <dd className={`font-serif text-xl ${b.highlight ? "text-[#ffde59]" : "text-white"}`}>
                    {b.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </>
      ) : (
        <div className="min-h-[260px] flex flex-col items-center justify-center text-center">
          <div className="h-14 w-14 rounded-full bg-[#ffde59]/15 inline-flex items-center justify-center mb-4">
            <i className="fa-solid fa-calculator text-2xl text-[#ffde59]" aria-hidden="true" />
          </div>
          <p className="text-white/70 text-sm max-w-xs leading-relaxed">{placeholder}</p>
        </div>
      )}
    </div>
  );
}

// ============================== Mortgage Calculator ==============================
const MORTGAGE_DEFAULT = { price: "", ltv: "75", tenure: "25", rate: "3.5" };

function MortgageCalc() {
  const [v, setV] = useState(MORTGAGE_DEFAULT);
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));
  const reset = () => setV(MORTGAGE_DEFAULT);

  const loan = (num(v.price) * num(v.ltv)) / 100;
  const monthly = monthlyPayment(loan, num(v.rate), num(v.tenure));
  const totalPaid = monthly * num(v.tenure) * 12;
  const totalInterest = totalPaid - loan;

  const filled = num(v.price) > 0 && num(v.tenure) > 0;

  return (
    <div className="grid lg:grid-cols-[1fr,1fr] gap-8" data-testid="mortgage-calculator">
      <div>
        <h3 className="font-serif text-2xl text-[#0a2540] mb-2">Mortgage Calculator</h3>
        <p className="text-sm text-slate-500 mb-7">
          Calculate your monthly mortgage payments and total interest over the loan tenure.
        </p>
        <div className="space-y-5">
          <Field label="Purchase Price" value={v.price} onChange={set("price")} prefix="S$" testid="mortgage-price" placeholder="1,500,000" />
          <Field label="Loan-to-Value Ratio" value={v.ltv} onChange={set("ltv")} suffix="%" testid="mortgage-ltv" hint="Typically up to 75% for first property." />
          <Field label="Loan Tenure" value={v.tenure} onChange={set("tenure")} suffix="years" testid="mortgage-tenure" />
          <Field label="Interest Rate" value={v.rate} onChange={set("rate")} suffix="% p.a." testid="mortgage-rate" />
          <button
            type="button"
            onClick={reset}
            data-testid="mortgage-reset"
            className="mt-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#0a2540] transition-colors"
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>
      <ResultPane
        filled={filled}
        placeholder="Enter your property details to calculate mortgage payments."
        primaryLabel="Estimated Monthly Repayment"
        primary={
          <span data-testid="mortgage-monthly">
            {fmt(monthly)}
            <span className="text-base text-white/60 font-sans"> /mo</span>
          </span>
        }
        breakdown={[
          { label: "Loan Amount", value: fmt(loan) },
          { label: "Total Repayable", value: fmt(totalPaid) },
          { label: "Total Interest", value: fmt(totalInterest), highlight: true },
        ]}
      />
    </div>
  );
}

// ============================== Sales Proceeds Calculator ==============================
const SALES_DEFAULT = {
  price: "",
  loan: "",
  cpf: "",
  holdingYears: "",
  agent: "",
  legal: "3000",
  others: "",
};

// Singapore GST applied on agent commission (9% from 1 Jan 2024).
const GST_RATE = 0.09;
// Default agent commission for residential resale = 2% of selling price.
const AGENT_FEE_RATE = 0.02;
const computeAgentFee = (price) =>
  Math.round(num(price) * AGENT_FEE_RATE * (1 + GST_RATE));

// Singapore Seller's Stamp Duty (SSD) for residential property
// New regulation revision:
//   Holding ≤ 1 year:        16%
//   Holding > 1 to 2 years:  12%
//   Holding > 2 to 3 years:   8%
//   Holding > 3 to 4 years:   4%
//   Holding > 4 years:        0%
function computeSSDRate(years) {
  const y = Number(years);
  if (!Number.isFinite(y) || y <= 0) return 0;
  if (y <= 1) return 0.16;
  if (y <= 2) return 0.12;
  if (y <= 3) return 0.08;
  if (y <= 4) return 0.04;
  return 0;
}

function computeSSD(price, years) {
  return Math.round(num(price) * computeSSDRate(years));
}

function SalesCalc() {
  const [v, setV] = useState(SALES_DEFAULT);
  const [agentEdited, setAgentEdited] = useState(false);
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));
  const reset = () => {
    setV(SALES_DEFAULT);
    setAgentEdited(false);
  };

  // Auto-fill Agent Fee = (2% of price) + 9% GST, unless user has manually overridden.
  useEffect(() => {
    if (!agentEdited) {
      const auto = num(v.price) > 0 ? String(computeAgentFee(v.price)) : "";
      setV((p) => (p.agent === auto ? p : { ...p, agent: auto }));
    }
  }, [v.price, agentEdited]);

  const ssdRate = computeSSDRate(v.holdingYears) * 100;
  const ssd = computeSSD(v.price, v.holdingYears);
  const totalDeduct =
    num(v.loan) +
    num(v.cpf) +
    num(v.agent) +
    num(v.legal) +
    num(v.others) +
    ssd;
  const proceeds = num(v.price) - totalDeduct;
  const filled = num(v.price) > 0;

  return (
    <div className="grid lg:grid-cols-[1fr,1fr] gap-8" data-testid="sales-proceed-calculator">
      <div>
        <h3 className="font-serif text-2xl text-[#0a2540] mb-2">Sales Proceeds Calculator</h3>
        <p className="text-sm text-slate-500 mb-7">
          Estimate the cash you will receive after selling your property, including Seller's Stamp Duty based on your holding period.
        </p>
        <div className="space-y-5">
          <Field label="Estimated Selling Price" value={v.price} onChange={set("price")} prefix="S$" testid="sales-price" placeholder="1,200,000" />
          <Field label="Outstanding Loan" value={v.loan} onChange={set("loan")} prefix="S$" testid="sales-loan" placeholder="400,000" />
          <Field label="CPF Used + Accrued Interest" value={v.cpf} onChange={set("cpf")} prefix="S$" testid="sales-cpf" placeholder="250,000" hint="Will be refunded to your CPF, not cash." />

          <Field
            label="Holding Period"
            value={v.holdingYears}
            onChange={set("holdingYears")}
            suffix="years"
            testid="sales-holding-years"
            placeholder="4"
            hint="Years from purchase to sale. Determines Seller's Stamp Duty (SSD)."
          />

          <ReadOnlyField
            label={`Seller's Stamp Duty (SSD) — Auto-calculated · ${ssdRate}%`}
            value={fmt(ssd).replace("S$", "")}
            testid="sales-ssd-auto"
            hint={
              ssdRate > 0
                ? `Holding ${num(v.holdingYears)} year${num(v.holdingYears) === 1 ? "" : "s"} — SSD applies at ${ssdRate}%.`
                : "Held more than 4 years — no SSD payable."
            }
          />

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Agent Fee"
              value={v.agent}
              onChange={(val) => {
                setAgentEdited(true);
                set("agent")(val);
              }}
              prefix="S$"
              testid="sales-agent-fee"
              placeholder="24,000"
              hint={
                agentEdited
                  ? "Manual override (auto: 2% of price + 9% GST)."
                  : "Auto: 2% of selling price + 9% GST. Editable."
              }
            />
            <Field
              label="Legal Fee"
              value={v.legal}
              onChange={set("legal")}
              prefix="S$"
              testid="sales-legal-fee"
              placeholder="3,000"
              hint="Default S$3,000. Editable."
            />
          </div>
          <Field label="Other Costs" value={v.others} onChange={set("others")} prefix="S$" testid="sales-others" placeholder="0" />
          <button
            type="button"
            onClick={reset}
            data-testid="sales-reset"
            className="mt-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#0a2540] transition-colors"
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>
      <ResultPane
        filled={filled}
        placeholder="Enter your sale details to estimate your cash proceeds."
        primaryLabel="Estimated Cash Proceeds"
        primary={
          <span data-testid="sales-proceeds" className={proceeds < 0 ? "text-red-400" : "text-white"}>
            {fmt(proceeds)}
          </span>
        }
        breakdown={[
          { label: "Gross Sale", value: fmt(num(v.price)) },
          { label: "Outstanding Loan", value: fmt(num(v.loan)) },
          { label: `SSD (${ssdRate}%)`, value: fmt(ssd) },
          { label: "Agent + Legal + Other", value: fmt(num(v.agent) + num(v.legal) + num(v.others)) },
          { label: "CPF Refund (non-cash)", value: fmt(num(v.cpf)), highlight: true },
          { label: "Total Deductions", value: fmt(totalDeduct) },
        ]}
      />
    </div>
  );
}

// ============================== Purchase Calculator ==============================
const PURCHASE_DEFAULT = {
  price: "",
  ltv: "75",
  tenure: "25",
  rate: "3.5",
  cashDown: "",          // S$ amount (user editable)
  buyingFor: "1",        // "1" | "2" | "3"
  buyerProfile: "SC",    // "SC" | "SPR" | "FOR" | "ENT"
};

// Singapore BSD calculation for residential property (tiered)
function computeBSD(price) {
  if (price <= 0) return 0;
  let bsd = 0;
  const brackets = [
    { upTo: 180000, rate: 0.01 },
    { upTo: 360000, rate: 0.02 },
    { upTo: 1000000, rate: 0.03 },
    { upTo: 1500000, rate: 0.04 },
    { upTo: 3000000, rate: 0.05 },
    { upTo: Infinity, rate: 0.06 },
  ];
  let prev = 0;
  for (const b of brackets) {
    if (price > b.upTo) {
      bsd += (b.upTo - prev) * b.rate;
      prev = b.upTo;
    } else {
      bsd += (price - prev) * b.rate;
      break;
    }
  }
  return Math.round(bsd);
}

// Singapore ABSD rates (residential, effective Apr 2023)
// SC: 1st 0%, 2nd 20%, 3rd+ 30%
// SPR: 1st 5%, 2nd 30%, 3rd+ 35%
// Foreigner: any 60%
// Entity: 65%
const ABSD_RATES = {
  SC:  { "1": 0,    "2": 0.20, "3": 0.30 },
  SPR: { "1": 0.05, "2": 0.30, "3": 0.35 },
  FOR: { "1": 0.60, "2": 0.60, "3": 0.60 },
  ENT: { "1": 0.65, "2": 0.65, "3": 0.65 },
};

const PROFILE_LABEL = {
  SC: "Singapore Citizen",
  SPR: "Singapore PR",
  FOR: "Foreigner",
  ENT: "Entity (Company, Trust, etc.)",
};

const BUYING_FOR_LABEL = {
  "1": "First Property",
  "2": "Second Property",
  "3": "Third+ Property",
};

function computeABSD(price, profile, buyingFor) {
  if (price <= 0) return 0;
  const rate = ABSD_RATES[profile]?.[buyingFor] ?? 0;
  return Math.round(price * rate);
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#0a2540] mb-2">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full appearance-none px-4 py-3.5 pr-10 border border-gray-200 rounded-xl text-sm text-[#0a2540] focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white transition-all"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none" aria-hidden="true" />
      </div>
    </label>
  );
}

function ReadOnlyField({ label, value, hint, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#0a2540] mb-2">{label}</span>
      <div className="relative">
        <div
          data-testid={testid}
          className="w-full pl-12 pr-4 py-3.5 border border-dashed border-[#ffde59] rounded-xl text-sm text-[#0a2540] bg-[#fffbea]/60 font-semibold"
        >
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium">S$</span>
          {value}
        </div>
      </div>
      {hint && <span className="block text-xs text-slate-400 mt-1.5">{hint}</span>}
    </label>
  );
}

function PurchaseCalc() {
  const [v, setV] = useState(PURCHASE_DEFAULT);
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));
  const reset = () => setV(PURCHASE_DEFAULT);

  const price = num(v.price);
  const ltv = Math.min(Math.max(num(v.ltv), 0), 100);
  const loanAmt = (price * ltv) / 100;
  const totalDownpayment = price - loanAmt;
  const minCashDown = Math.round(price * 0.05); // SG: minimum 5% must be in cash
  // Auto-fill cashDown with 5% when price first entered or if current value is below 5%.
  useEffect(() => {
    if (price <= 0) return;
    const current = num(v.cashDown);
    if (v.cashDown === "" || current < minCashDown) {
      setV((p) => ({ ...p, cashDown: String(minCashDown) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.price]);
  // On blur, enforce floor of 5% — user cannot reduce below it.
  const clampCashDown = () => {
    if (price <= 0) return;
    const current = num(v.cashDown);
    if (current < minCashDown) {
      setV((p) => ({ ...p, cashDown: String(minCashDown) }));
    }
  };
  const cashDown = price > 0 ? Math.min(Math.max(num(v.cashDown), minCashDown), totalDownpayment) : 0;
  const cpfDown = Math.max(totalDownpayment - cashDown, 0); // auto-calculated
  const bsd = computeBSD(price);
  const absd = computeABSD(price, v.buyerProfile, v.buyingFor);
  const absdRate = (ABSD_RATES[v.buyerProfile]?.[v.buyingFor] ?? 0) * 100;
  const upfrontCash = cashDown + bsd + absd; // CPF is non-cash but still part of downpayment
  const totalUpfront = totalDownpayment + bsd + absd;
  const monthly = monthlyPayment(loanAmt, num(v.rate), num(v.tenure));
  const filled = price > 0;

  return (
    <div className="grid lg:grid-cols-[1fr,1fr] gap-8" data-testid="purchase-calculator">
      <div>
        <h3 className="font-serif text-2xl text-[#0a2540] mb-2">Purchase Calculator</h3>
        <p className="text-sm text-slate-500 mb-7">
          Estimate your total upfront cost — cash, CPF, BSD, ABSD, and monthly repayment — based on your buyer profile.
        </p>

        <div className="space-y-5">
          <Field
            label="Purchase Price"
            value={v.price}
            onChange={set("price")}
            prefix="S$"
            testid="purchase-price"
            placeholder="1,500,000"
          />

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Loan-to-Value Ratio"
              value={v.ltv}
              onChange={set("ltv")}
              suffix="%"
              testid="purchase-ltv"
              hint="Typically up to 75%"
            />
            <Field
              label="Loan Tenure"
              value={v.tenure}
              onChange={set("tenure")}
              suffix="years"
              testid="purchase-tenure"
            />
          </div>

          <Field
            label="Interest Rate"
            value={v.rate}
            onChange={set("rate")}
            suffix="% p.a."
            testid="purchase-rate"
          />

          <Field
            label="Cash Downpayment"
            value={v.cashDown}
            onChange={set("cashDown")}
            onBlur={clampCashDown}
            prefix="S$"
            testid="purchase-cash"
            placeholder={filled ? minCashDown.toLocaleString("en-SG") : "75,000"}
            hint="Auto-filled at minimum 5% of purchase price. You can increase it — values below 5% are not allowed."
          />

          <ReadOnlyField
            label="CPF Downpayment (Auto-calculated)"
            value={fmt(cpfDown).replace("S$", "")}
            testid="purchase-cpf-auto"
            hint="Total downpayment minus cash. Subject to CPF usage limits."
          />

          {/* Buyer Profile section */}
          <div className="pt-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#0a2540]/70 font-semibold mb-4">
              Buyer Profile
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Buying for"
                value={v.buyingFor}
                onChange={set("buyingFor")}
                testid="purchase-buying-for"
                options={[
                  { value: "1", label: "First Property" },
                  { value: "2", label: "Second Property" },
                  { value: "3", label: "Third+ Property" },
                ]}
              />
              <Select
                label="Buyer Profile"
                value={v.buyerProfile}
                onChange={set("buyerProfile")}
                testid="purchase-buyer-profile"
                options={[
                  { value: "SC", label: "Singapore Citizen" },
                  { value: "SPR", label: "Singapore PR" },
                  { value: "FOR", label: "Foreigner" },
                  { value: "ENT", label: "Entity (Company, Trust, etc.)" },
                ]}
              />
            </div>
          </div>

          <ReadOnlyField
            label="Buyer's Stamp Duty (BSD) — Auto-calculated"
            value={fmt(bsd).replace("S$", "")}
            testid="purchase-bsd-auto"
            hint="Tiered BSD based on purchase price."
          />

          <ReadOnlyField
            label="Additional Buyer's Stamp Duty (ABSD) — Auto-calculated"
            value={fmt(absd).replace("S$", "")}
            testid="purchase-absd-auto"
            hint={`${PROFILE_LABEL[v.buyerProfile]} · ${BUYING_FOR_LABEL[v.buyingFor]} · ABSD rate ${absdRate}%`}
          />

          <button
            type="button"
            onClick={reset}
            data-testid="purchase-reset"
            className="mt-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#0a2540] transition-colors"
          >
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>

      <ResultPane
        filled={filled}
        placeholder="Enter your purchase details to estimate upfront costs, stamp duties, and monthly repayment."
        primaryLabel="Estimated Total Upfront"
        primary={
          <span data-testid="purchase-upfront">{fmt(totalUpfront)}</span>
        }
        breakdown={[
          { label: "Loan Amount", value: fmt(loanAmt) },
          { label: "Cash Downpayment", value: fmt(cashDown) },
          { label: "CPF Downpayment", value: fmt(cpfDown) },
          { label: "Buyer's Stamp Duty", value: fmt(bsd) },
          { label: `ABSD (${absdRate}%)`, value: fmt(absd) },
          {
            label: "Cash Required",
            value: <span data-testid="purchase-cash-required">{fmt(upfrontCash)}</span>,
            highlight: true,
          },
          {
            label: "Monthly Repayment",
            value: (
              <span data-testid="purchase-monthly">
                {fmt(monthly)} <span className="text-sm text-white/60">/mo</span>
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}

// ============================== Page ==============================
const TABS = [
  { id: "mortgage", label: "Mortgage Calculator", icon: "fa-solid fa-percent" },
  { id: "sales", label: "Sales Proceeds Calculator", icon: "fa-solid fa-hand-holding-dollar" },
  { id: "purchase", label: "Purchase Calculator", icon: "fa-solid fa-cart-shopping" },
];

export default function Calculators() {
  const [tab, setTab] = useState("mortgage");

  return (
    <>
      <SEO {...pageMeta.calculators} />

      {/* Hero */}
      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="calculators-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Resources
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Financial Calculators
            </h1>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Make informed property decisions with our easy-to-use calculators.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-[#fafafa] pt-10 pb-20 md:pt-12 md:pb-28" data-testid="calculators-tabs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 mb-10" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                data-testid={`calc-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-5 sm:px-6 py-3 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  tab === t.id
                    ? "bg-[#0a2540] text-white shadow-lg"
                    : "bg-white text-slate-600 border border-gray-200 hover:border-[#0a2540]"
                }`}
              >
                <i className={t.icon} aria-hidden="true" />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.label.replace(" Calculator", "")}</span>
              </button>
            ))}
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-6 md:p-10 shadow-sm">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
              >
                {tab === "mortgage" && <MortgageCalc />}
                {tab === "sales" && <SalesCalc />}
                {tab === "purchase" && <PurchaseCalc />}
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="mt-8 text-center text-xs text-slate-500 max-w-3xl mx-auto leading-relaxed">
            <strong className="text-[#0a2540]">Disclaimer:</strong> These calculators provide estimates for informational purposes only. Actual figures may vary based on specific circumstances, bank policies, and market conditions. If you'd like advice tailored to your situation, feel free to reach out to Johnny.
          </p>
        </div>
      </section>

      <CTABanner headline="Need help interpreting these numbers?" subcopy="Numbers only tell part of the story. Let's review them in the context of your timeline, family, and long-term wealth plan." />
    </>
  );
}
